import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Calendar, Settings, LogOut, Menu, X, Shield, ListChecks } from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { path: '/routine', label: 'My Routine', icon: ListChecks, testId: 'nav-routine' },
  { path: '/history', label: 'History', icon: Calendar, testId: 'nav-history' },
  { path: '/settings', label: 'Settings', icon: Settings, testId: 'nav-settings' },
];

const adminNavItem = { path: '/admin', label: 'Admin', icon: Shield, testId: 'nav-admin' };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isEmbed = searchParams.get('embed') === 'true';

  if (isEmbed) {
    const embedNavItems = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/routine', label: 'My Routine', icon: ListChecks },
      { path: '/history', label: 'History', icon: Calendar },
      { path: '/settings', label: 'Settings', icon: Settings },
    ];

    return (
      <div className="min-h-screen bg-[--hc-bg]" data-testid="embed-layout">
        {/* Compact tab nav */}
        <div className="sticky top-0 z-30 bg-white border-b border-[--hc-border] px-4">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {embedNavItems.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`embed-nav-${item.path.slice(1)}`}
                  onClick={() => navigate(`${item.path}?embed=true`)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-[--hc-accent] text-[--hc-primary]'
                      : 'border-transparent text-[--hc-text-muted] hover:text-[--hc-text] hover:border-[--hc-border]'
                  }`}
                >
                  <item.icon size={14} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="px-5 py-4">
          {children}
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[--hc-bg] flex" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-[260px] min-h-screen border-r border-[--hc-border] bg-white" data-testid="app-sidebar">
        <div className="p-6 pb-4">
          <img src="/logo.png" alt="HabitCloser" className="h-8 w-auto" />
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {[...navItems, ...(user?.is_admin ? [adminNavItem] : [])].map(item => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={item.testId}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? 'bg-[--hc-accent-soft] text-[--hc-primary] border-l-[3px] border-[--hc-accent]'
                    : 'text-[--hc-text-muted] hover:bg-[--hc-surface] hover:text-[--hc-text]'
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[--hc-border]">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[--hc-primary] flex items-center justify-center text-white text-xs font-semibold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[--hc-text] truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-[--hc-text-muted] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="nav-logout"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[--hc-text-muted] hover:bg-[--hc-danger-soft] hover:text-[--hc-danger] transition-colors duration-150"
          >
            <LogOut size={18} />
            Log out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="flex-1 flex flex-col">
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[--hc-border] bg-white sticky top-0 z-40" data-testid="mobile-topbar">
          <div className="flex items-center gap-2">
            <button
              data-testid="mobile-nav-open-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded-lg hover:bg-[--hc-surface] transition-colors"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <img src="/icon.png" alt="HabitCloser" className="h-6 w-6" />
            <span className="text-lg font-bold text-[--hc-primary]">HabitCloser</span>
          </div>
          <div className="w-7 h-7 rounded-full bg-[--hc-primary] flex items-center justify-center text-white text-xs font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-[53px] bg-white z-30 p-4">
            <nav className="space-y-1">
              {[...navItems, ...(user?.is_admin ? [adminNavItem] : [])].map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    data-testid={`mobile-${item.testId}`}
                    onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[--hc-accent-soft] text-[--hc-primary]'
                        : 'text-[--hc-text-muted] hover:bg-[--hc-surface]'
                    }`}
                  >
                    <item.icon size={20} />
                    {item.label}
                  </button>
                );
              })}
              <button
                data-testid="mobile-nav-logout"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[--hc-danger] hover:bg-[--hc-danger-soft] transition-colors"
              >
                <LogOut size={20} />
                Log out
              </button>
            </nav>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6 md:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
